import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { LiveDriver } from '~/data-classes';
import { SupportedSuggestMetric } from '~/api/SuggestApi';
import {
    useToastContext,
    useBulkSuggest,
    useOnDemandDispatchAssignTasks,
    defaultBulkSuggestOptions
} from '~/hooks';

import {
    addSelectedMapRoute,
    resetSelectedMapRoutes
} from '~/reducers/selectedMapRoutesSlice';

import {
    setHasIsolatedRoutes,
    setIsMultipleCardSelectEnabled,
    setShouldFitPlanMapToBounds,
    setViewCardDetails
} from '~/reducers/mapSettingsSlice';

import {
    setIsOpenSelectedCardsDrawer,
    setShowDrawerOnDemandDispatch,
    setIsSuggestDriversDrawer
} from '~/reducers/mapDrawerSettingsSlice';
import { selectDate } from '~/reducers/selectedDateSlice';

import { colorUtils } from '~/utils/color-utils';
import { idUtils } from '~/utils/id-utils';

import {
    Alert,
    ManageStopControlModal,
    Spinner,
    SuggestedDriverCard,
    TooltipButton
} from '~/ui';
import { selectLiveDrivers } from '~/reducers/liveDriversSlice';
import { resetSelectedMapStops } from '~/reducers/selectedMapStopsSlice';

function DispatchTasksControl({
    selectedTaskIds,
    modalTitle,
    onClickTitle = () => {}
}) {
    const orderBy = SupportedSuggestMetric.TOTAL_ROUTE_TIME;
    const [suggestions, setSuggestions] = useState();
    const [taskIds, setTaskIds] = useState(selectedTaskIds);
    const [viewAll, setViewAll] = useState(false);
    const [dispatchData, setDispatchData] = useState();
    const selectedDate = useSelector(selectDate);
    const liveDrivers = useSelector(selectLiveDrivers);
    const dispatch = useDispatch();
    const { t } = useTranslation(['translation', 'taskManagement']);
    const isFetching = Boolean(useIsFetching());
    const { addToast } = useToastContext();

    const {
        isSuccess: suggestApiIsSuccess,
        isError: suggestApiIsError,
        error: suggestApiError
    } = useBulkSuggest(taskIds, defaultBulkSuggestOptions, {
        enabled: !!taskIds,
        onSuccess: _handleSuccessBulkSuggest
    });

    const { isError: assignTasksIsError, error: assignTasksError } =
        useOnDemandDispatchAssignTasks(dispatchData, {
            enabled: !!dispatchData,
            onSuccess: _handleSuccessAssignTasks
        });

    useEffect(() => {
        if (!suggestions?.length) return;
        const bulkSuggestDriverId = suggestions[0].driver.id;
        const suggestedDriver = Object.values(liveDrivers).find(
            (driver) => driver.id === bulkSuggestDriverId
        );
        if (!suggestedDriver) return;
        const suggestedLiveDriver = new LiveDriver(suggestedDriver);
        const { clientDriverId } = suggestedLiveDriver;
        _handleTopSuggestion(clientDriverId);
    }, [suggestions]);

    function _getListItems(data) {
        return data.map((suggestion, idx) => {
            const clientDriverId = idUtils.getCombinedId(
                suggestion.driver.client,
                suggestion.driver.id
            );
            const driverColors = colorUtils.getWebColorsForId(clientDriverId);
            const isBestDriver = idx === 0;
            return (
                <SuggestedDriverCard
                    key={clientDriverId}
                    suggestedDriverData={suggestion}
                    clientDriverId={clientDriverId}
                    driverColors={driverColors}
                    orderedBy={orderBy}
                    isBestDriver={isBestDriver}
                    onClick={_handleClickDispatch}
                />
            );
        });
    }

    function _getError(errorData) {
        return (
            <Alert
                className="dispatch-tasks-control_error"
                variant="danger-dark"
                icon="warningFill2"
                iconSize="s"
                iconColor="mars"
            >
                {errorData?.response?.data?.message || errorData.message}
            </Alert>
        );
    }

    function _handleTopSuggestion(clientDriverId) {
        dispatch(setIsSuggestDriversDrawer(true));
        dispatch(setHasIsolatedRoutes(true));
        dispatch(setShowDrawerOnDemandDispatch(true));
        dispatch(setIsOpenSelectedCardsDrawer(false));
        dispatch(addSelectedMapRoute(clientDriverId));
        dispatch(setShouldFitPlanMapToBounds(true));
    }

    function _handleClickViewAll() {
        setViewAll(true);
        dispatch(setIsMultipleCardSelectEnabled(true));
        dispatch(setViewCardDetails(true));
        if (suggestions) {
            const suggestedDrivers = suggestions.map((suggestion) => {
                return suggestion.driver.id;
            });
            // eslint-disable-next-line array-callback-return
            Object.values(liveDrivers).map((driver) => {
                if (suggestedDrivers.includes(driver.id)) {
                    const suggestedLiveDriver = new LiveDriver(driver);
                    const { clientDriverId } = suggestedLiveDriver;
                    dispatch(addSelectedMapRoute(clientDriverId));
                    dispatch(setShouldFitPlanMapToBounds(true));
                }
            });
        }
    }

    function _handleClickDispatch(payload) {
        // trigger the assign tasks hook
        setDispatchData({ selectedDate, ...payload });
    }

    function _handleSuccessBulkSuggest(data) {
        // display view all with more than 1 result
        setViewAll(data.length <= 1);

        // set suggestions for display
        setSuggestions(data);
    }

    function _handleSuccessAssignTasks(data) {
        // disable the bulk suggest hook
        setTaskIds();

        // clear suggestions
        setSuggestions();

        // set success toast message
        const key = data.payload.isLiveDriver
            ? 'taskManagement:assignTask.dispatchConfirmation'
            : 'taskManagement:assignTask.assignConfirmation';
        addToast({
            message: t(key, { count: data.payload.tasksToDispatch.length })
        });

        // closes the ODD and suggest drawers and control
        dispatch(setShowDrawerOnDemandDispatch(false));
        dispatch(setIsSuggestDriversDrawer(false));

        // resets selected map routes so the map is cleared when user is redirected
        dispatch(resetSelectedMapRoutes());
        dispatch(resetSelectedMapStops());
        dispatch(setViewCardDetails(false));
        onClickTitle();
    }

    function _resetStopControlModal() {
        dispatch(setIsSuggestDriversDrawer(false));
        onClickTitle();
        dispatch(resetSelectedMapRoutes());
    }

    return (
        <ManageStopControlModal
            className="dispatch-tasks-control"
            modalTitle={modalTitle}
            onClickTitle={_resetStopControlModal}
        >
            {isFetching && (
                <div className="dispatch-tasks-control_loading _d-flex _jc-center _ai-center">
                    <Spinner
                        sx={{ width: '2rem', height: '2rem' }}
                        color="ocean"
                    />
                </div>
            )}
            {!isFetching && suggestApiIsSuccess && suggestions && (
                <>
                    {suggestions.length > 0 && (
                        <>
                            <div className="dispatch-tasks-control_text _text-3-alt">
                                {t(
                                    'UnassignedTasksControl.title.suggested_routes'
                                )}
                            </div>
                            <div className="dispatch-tasks-control_list _text-3-alt">
                                {_getListItems(
                                    viewAll ? suggestions : [suggestions[0]]
                                )}
                            </div>
                        </>
                    )}
                    {!suggestions.length &&
                        _getError({
                            message: t(
                                'UnassignedTasksControl.error.no_drivers'
                            )
                        })}
                    {!viewAll && (
                        <TooltipButton
                            className="dispatch-tasks-control_view-all _text-3 button_clear"
                            onClick={_handleClickViewAll}
                        >
                            {t('UnassignedTasksControl.button.viewAll')}
                        </TooltipButton>
                    )}
                    {viewAll && (
                        <div className="dispatch-tasks-control_footer _text-3-alt">
                            {t('UnassignedTasksControl.title.suggestions_info')}
                        </div>
                    )}
                </>
            )}
            {!isFetching && suggestApiIsError && _getError(suggestApiError)}
            {!isFetching && assignTasksIsError && _getError(assignTasksError)}
        </ManageStopControlModal>
    );
}

export default DispatchTasksControl;
